// This file is auto-generated by @hey-api/openapi-ts

import { z } from 'zod';

/**
 * Discriminator for the three fixed period columns. The
 * rendering order is always Current → Renewal → Revised
 * Renewal regardless of array position; the `periods`
 * object on the response uses these as fixed property
 * names.
 *
 */
export const zPeriodKey = z.enum([
    'current',
    'renewal',
    'revisedRenewal'
]);

/**
 * Indicates the source of the per-axis ordering and
 * visibility values returned by `GET`.
 *
 * - `page` — values come from this Financials page's own
 * persisted state.
 * - `global` — values come from the broker's global
 * preferences applied across all *Present* sub-tabs.
 *
 */
export const zAppliedFrom = z.enum(['page', 'global']);

/**
 * Promotion target for a per-axis state change submitted via
 * PATCH.
 *
 * - `page` — explicit page-local write (equivalent to
 * omitting `view.<axis>ApplyTo`).
 * - `global` — promote the submitted state for this axis
 * to the broker's global preferences. Only set by the
 * "Apply to all pages" sidebar action.
 *
 */
export const zApplyTo = z.enum(['page', 'global']);

/**
 * Indicates how a coverage is funded.
 *
 * - `fullyInsured` — traditional fully-insured coverage
 * where the carrier bears the risk.
 * - `selfFunded` — self-funded (ASO) coverage where the
 * employer bears the risk and the carrier provides
 * administrative services only.
 *
 */
export const zFundingType = z.enum(['fullyInsured', 'selfFunded']);

/**
 * Sparse coverage metadata edit. Only the fields included
 * are updated; per-cell premium values are sent under
 * `periods` or `carriers`, not here.
 *
 */
export const zCoverageMetadataEdit = z.object({
    id: z.string().uuid(),
    hidden: z.boolean().optional(),
    order: z.number().int().gte(0).optional(),
    notesRowHeaderLabel: z.string().max(140).optional()
});

export const zPeriodCoverageCellWithPremium = z.object({
    annualPremium: z.number().int().gte(0),
    premiumOverridden: z.boolean().optional(),
    note: z.string().optional()
});

export const zPeriodCoverageCellWithNote = z.object({
    annualPremium: z.number().int().gte(0).optional(),
    note: z.string()
});

/**
 * Values for a single coverage in a single period. At least
 * one of `annualPremium` / `note` is present (otherwise the
 * cell would not be in the map).
 *
 */
export const zPeriodCoverageCell = z.union([
    zPeriodCoverageCellWithPremium,
    zPeriodCoverageCellWithNote
]);

export const zPeriodColumn = z.object({
    hidden: z.boolean(),
    coverages: z.record(zPeriodCoverageCell)
});

/**
 * Fixed object holding the three period columns. Render
 * order is always Current → Renewal → Revised Renewal.
 *
 */
export const zPeriodColumns = z.object({
    current: zPeriodColumn,
    renewal: zPeriodColumn,
    revisedRenewal: zPeriodColumn
});

export const zPeriodCoverageCellEdit = z.object({
    annualPremium: z.number().int().gte(0).nullish(),
    note: z.string().max(1024).nullish()
});

/**
 * Sparse edit to a single period column. Omit `coverages`
 * to leave per-cell values untouched; omit `hidden` to
 * leave visibility untouched.
 *
 */
export const zPeriodColumnEdit = z.object({
    hidden: z.boolean().optional(),
    coverages: z.record(zPeriodCoverageCellEdit).optional()
});

/**
 * Sparse edits to the period columns. Omit a period to
 * leave it untouched.
 *
 */
export const zPeriodColumnsEdit = z.object({
    current: zPeriodColumnEdit.optional(),
    renewal: zPeriodColumnEdit.optional(),
    revisedRenewal: zPeriodColumnEdit.optional()
});

export const zCarrierCoverageCellWithPremium = z.object({
    annualPremium: z.number().int().gte(0),
    premiumOverridden: z.boolean().optional(),
    note: z.string().max(1024).optional()
});

export const zCarrierCoverageCellWithNote = z.object({
    annualPremium: z.number().int().gte(0).optional(),
    note: z.string().max(1024)
});

/**
 * Carrier-quoted values for a single coverage. At least
 * one of `annualPremium` / `note` is present (otherwise
 * the cell would not be in the map).
 *
 */
export const zCarrierCoverageCell = z.union([
    zCarrierCoverageCellWithPremium,
    zCarrierCoverageCellWithNote
]);

export const zCarrierColumn = z.object({
    id: z.string(),
    name: z.string(),
    hidden: z.boolean(),
    order: z.number().int().gte(0),
    coverages: z.record(zCarrierCoverageCell)
});

export const zCarrierCoverageCellEdit = z.object({
    annualPremium: z.number().int().gte(0).nullish(),
    note: z.string().max(1024).nullish()
});

/**
 * Sparse carrier edit. Only the fields included are updated.
 *
 */
export const zCarrierColumnEdit = z.object({
    id: z.string(),
    name: z.string().optional(),
    hidden: z.boolean().optional(),
    order: z.number().int().gte(0).optional(),
    coverages: z.record(zCarrierCoverageCellEdit).optional()
});

/**
 * Map of period key → free-text note for this totals
 * block in that period column. Sparse: absent periods
 * have no note. Send `null` in a PATCH to clear.
 *
 */
export const zTotalsBlockPeriodNotes = z.object({
    current: z.string().max(1024).nullish(),
    renewal: z.string().max(1024).nullish(),
    revisedRenewal: z.string().max(1024).nullish()
});

/**
 * Map of carrier ID → free-text note for this totals
 * block in that carrier column. Sparse: absent carriers
 * have no note. Send `null` in a PATCH to clear.
 *
 */
export const zTotalsBlockCarrierNotes = z.record(z.string().max(1024).nullable());

/**
 * Map of period key → whole-dollar credit amount. Sparse:
 * absent periods have no entered value.
 *
 */
export const zCreditsRowPeriodValues = z.object({
    current: z.number().int().gte(1).optional(),
    renewal: z.number().int().gte(1).optional(),
    revisedRenewal: z.number().int().gte(1).optional()
});

/**
 * Map of carrier ID → whole-dollar credit amount. Sparse:
 * absent carriers have no entered value.
 *
 */
export const zCreditsRowCarrierValues = z.record(z.number().int().gte(1));

/**
 * Per-block credits/subsidy row. Always present on every
 * totals block. When `hidden` is true the row is not
 * rendered and its values are excluded from totals.
 * `label` is analyst-editable (e.g., "Technology Credits").
 *
 */
export const zCreditsRow = z.object({
    hidden: z.boolean(),
    label: z.string().max(140),
    periodValues: zCreditsRowPeriodValues.optional(),
    carrierValues: zCreditsRowCarrierValues.optional()
});

/**
 * Sparse edit to period credit values. `null` clears the
 * value for that period.
 *
 */
export const zCreditsRowPeriodValuesEdit = z.object({
    current: z.number().int().gte(1).nullish(),
    renewal: z.number().int().gte(1).nullish(),
    revisedRenewal: z.number().int().gte(1).nullish()
});

/**
 * Map of carrier ID → credit value edit. `null` clears
 * the value for that carrier.
 *
 */
export const zCreditsRowCarrierValuesEdit = z.record(z.number().int().gte(1).nullable());

/**
 * Sparse edit to the credits row. Omit fields to leave
 * them untouched. Send `null` for a value entry to clear it.
 *
 */
export const zCreditsRowEdit = z.object({
    hidden: z.boolean().optional(),
    label: z.string().max(140).optional(),
    periodValues: zCreditsRowPeriodValuesEdit.optional(),
    carrierValues: zCreditsRowCarrierValuesEdit.optional()
});

export const zTotalsBlockCoverageRef = z.object({
    coverageId: z.string().uuid(),
    order: z.number().int().gte(0)
});

/**
 * A named subset of coverages whose premiums roll up into
 * a dedicated totals row group.
 *
 * A coverage may belong to at most one block.
 *
 */
export const zCustomTotalsBlock = z.object({
    kind: z.enum(['custom']),
    id: z.string().uuid(),
    name: z.string().max(140),
    periodNotes: zTotalsBlockPeriodNotes.optional(),
    carrierNotes: zTotalsBlockCarrierNotes.optional(),
    creditsRow: zCreditsRow,
    notesRowHeaderLabel: z.string().max(140).optional(),
    coverages: z.array(zTotalsBlockCoverageRef),
    order: z.number().int().gte(0)
});

/**
 * The default totals block. Always present, always
 * rendered last. Cannot reordered.
 *
 */
export const zDefaultTotalsBlock = z.object({
    kind: z.enum(['default']),
    id: z.enum(['default']),
    name: z.string().max(140).optional(),
    periodNotes: zTotalsBlockPeriodNotes.optional(),
    carrierNotes: zTotalsBlockCarrierNotes.optional(),
    creditsRow: zCreditsRow,
    notesRowHeaderLabel: z.string().max(140).optional(),
    coverages: z.array(zTotalsBlockCoverageRef)
});

export const zTotalsBlock = z.union([
    zCustomTotalsBlock,
    zDefaultTotalsBlock
]);

export const zTotalsBlockCoverageRefEdit = z.object({
    coverageId: z.string().uuid(),
    order: z.number().int().gte(0)
});

/**
 * Sparse edit to a custom totals block. Only `kind` and
 * `id` are required; include only the fields that should
 * change.
 *
 */
export const zCustomTotalsBlockEdit = z.object({
    kind: z.enum(['custom']),
    id: z.string().uuid(),
    name: z.string().max(140).optional(),
    periodNotes: zTotalsBlockPeriodNotes.optional(),
    carrierNotes: zTotalsBlockCarrierNotes.optional(),
    creditsRow: zCreditsRowEdit.optional(),
    notesRowHeaderLabel: z.string().max(140).optional(),
    coverages: z.array(zTotalsBlockCoverageRefEdit).optional(),
    order: z.number().int().gte(0).optional()
});

/**
 * Sparse edit to the default totals block. Only `kind`
 * and `id` are required; include only the fields that
 * should change.
 *
 */
export const zDefaultTotalsBlockEdit = z.object({
    kind: z.enum(['default']),
    id: z.enum(['default']),
    name: z.string().max(140).optional(),
    periodNotes: zTotalsBlockPeriodNotes.optional(),
    carrierNotes: zTotalsBlockCarrierNotes.optional(),
    creditsRow: zCreditsRowEdit.optional(),
    notesRowHeaderLabel: z.string().max(140).optional(),
    coverages: z.array(zTotalsBlockCoverageRefEdit).optional()
});

export const zTotalsBlockEdit = z.union([
    zCustomTotalsBlockEdit,
    zDefaultTotalsBlockEdit
]);

/**
 * A custom totals block to create.
 */
export const zNewTotalsBlock = z.object({
    label: z.string().min(1).max(140),
    position: z.number().int().gte(0),
    coverageIds: z.array(z.string().uuid()).optional()
});

/**
 * Per-axis indication of where the returned ordering and
 * visibility values originated. Period columns are always
 * page-local and have no entry here.
 *
 */
export const zViewState = z.object({
    coveragesAppliedFrom: zAppliedFrom,
    carriersAppliedFrom: zAppliedFrom
});

/**
 * Per-axis promotion targets. Each `*ApplyTo` field is only
 * valid when the same PATCH includes the corresponding
 * `coverages` / `carriers` payload.
 *
 */
export const zViewStateEdit = z.object({
    coveragesApplyTo: zApplyTo.optional(),
    carriersApplyTo: zApplyTo.optional()
});

/**
 * Broker-scoped display preferences resolved server-side.
 * Read-only via this contract — managed elsewhere.
 *
 */
export const zBrokerConfig = z.object({
    showDollarSavings: z.boolean()
});

/**
 * Opaque AG Grid presentation state (column widths, etc.)
 * round-tripped unchanged through MPX's grid-state
 * mechanism. The FE serializes / deserializes; the BFF
 * treats it as a passthrough blob.
 *
 */
export const zTablePreferences = z.record(z.unknown());

/**
 * Sparse mirror of `FinancialsTableResponse`. Every key is
 * optional. See the `PATCH` operation description for sparse
 * semantics and validation rules.
 *
 */
export const zPatchFinancialsTableRequest = z.object({
    coverages: z.array(zCoverageMetadataEdit).optional(),
    periods: zPeriodColumnsEdit.optional(),
    carriers: z.array(zCarrierColumnEdit).optional(),
    totalsBlocks: z.array(zTotalsBlockEdit).optional(),
    newTotalsBlocks: z.array(zNewTotalsBlock).optional(),
    deleteTotalsBlockIds: z.array(z.string().uuid()).optional(),
    view: zViewStateEdit.optional(),
    tablePreferences: zTablePreferences.nullish()
});

/**
 * Types of coverage, aka line of coverage (LoC). We don't see standalone AD&D often. However, it is still a valid coverage type that is usually part of life plans.
 */
export const zCoverageTypeEnum = z.enum([
    'dental',
    'vision',
    'std',
    'ltd',
    'basicLifeAdnd',
    'voluntaryLifeAdnd',
    'accident',
    'criticalIllness',
    'hospitalIndemnity',
    'cancer',
    'fmla',
    'eap',
    'individualDisabilityInsurance',
    'wholeLife'
]);

export const zCoverageType = zCoverageTypeEnum;

export const zCoverageRef = z.object({
    id: z.string().uuid(),
    name: z.string(),
    coverageType: zCoverageTypeEnum,
    isVirgin: z.boolean(),
    fundingType: zFundingType,
    hidden: z.boolean(),
    order: z.number().int().gte(0),
    notesRowHeaderLabel: z.string().max(140).optional()
});

export const zFinancialsTableResponse = z.object({
    rfpId: z.string(),
    coverages: z.array(zCoverageRef),
    periods: zPeriodColumns,
    carriers: z.array(zCarrierColumn),
    totalsBlocks: z.array(zTotalsBlock),
    view: zViewState,
    brokerConfig: zBrokerConfig,
    tablePreferences: zTablePreferences.nullish()
});

/**
 * Override the RFP specification version for this request.
 * When omitted, the default version from the global feature
 * flag is used.
 *
 */
export const zRfpSpecVersion = z.string();

export const zGetFinancialsTableHeaders = z.object({
    'X-RFP-Spec-Version': z.string().optional()
});

export const zGetFinancialsTablePath = z.object({
    id: z.string()
});

/**
 * Financials table data for the RFP
 */
export const zGetFinancialsTableResponse = zFinancialsTableResponse;

export const zPatchFinancialsTableBody = zPatchFinancialsTableRequest;

export const zPatchFinancialsTableHeaders = z.object({
    'X-RFP-Spec-Version': z.string().optional()
});

export const zPatchFinancialsTablePath = z.object({
    id: z.string()
});

/**
 * Update applied
 */
export const zPatchFinancialsTableResponse = z.void();
